/**
 * Composant pour créer une souscription FTPE pour un membre
 * Utilisable par Super Admin, COOPER'S et Expert
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UserPlus, Search, Building, CheckCircle, AlertTriangle, RefreshCw, Users, FileText, CreditCard, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PhoneInputWithFlagSVG from './PhoneInputWithFlagSVG';
import TerritorySelector from './TerritorySelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FTPESubscriptionForMember({ onSuccess: onSuccess }) {
  const { user: user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, _setSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Formulaire
  const [form, setForm] = useState({
    raison_sociale: '',
    siret: '',
    adresse: '',
    territoire: 'Guadeloupe',
    telephone: '',
    email: '',
    representant_nom: '',
    type_adhesion: 'tpe'
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    // Filtrer les membres
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredMembers(members.filter(m => 
        (m.email || '').toLowerCase().includes(query) ||
        (m.entreprise || '').toLowerCase().includes(query) ||
        (m.prenom || '').toLowerCase().includes(query) ||
        (m.nom || '').toLowerCase().includes(query)
      ));
    } else {
      setFilteredMembers(members.slice(0, 20));
    }
  }, [searchQuery, members]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/membres`,
        { headers: getAuthHeader() }
      );
      // Filtrer les membres qui n'ont pas encore d'adhésion FTPE active
      const allMembers = res.data.members || res.data.items || res.data || [];
      // Trier par date de création (plus récent au plus ancien)
      const sortedMembers = allMembers.sort((a, b) => 
        new Date(b.created_at || b.date_creation || 0) - new Date(a.created_at || a.date_creation || 0)
      );
      setMembers(sortedMembers);
      setFilteredMembers(sortedMembers.slice(0, 20));
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const selectMember = (member) => {
    setSelectedMember(member);
    setForm({
      raison_sociale: member.entreprise || '',
      siret: member.siret || '',
      adresse: member.adresse || '',
      territoire: member.territoire || 'Guadeloupe',
      telephone: member.telephone || '',
      email: member.email || '',
      representant_nom: `${member.prenom || ''} ${member.nom || ''}`.trim(),
      type_adhesion: 'tpe'
    });
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = () => {
    if (!selectedMember) return;
    
    // Fermer le dialog
    setShowDialog(false);
    
    // Construire l'URL avec les parametres du membre pour pre-remplir le formulaire
    const params = new URLSearchParams({
      for_member: 'true',
      member_id: selectedMember.id || selectedMember._id || '',
      member_name: form.representant_nom || `${selectedMember.prenom || ''} ${selectedMember.nom || ''}`.trim(),
      member_email: form.email || selectedMember.email || '',
      member_phone: form.telephone || selectedMember.telephone || '',
      member_entreprise: form.raison_sociale || selectedMember.entreprise || '',
      member_territoire: form.territoire || selectedMember.territoire || 'Guadeloupe'
    });
    
    // Naviguer vers le workflow d'adhesion FTPE avec les donnees pre-remplies
    navigate(`/adhesion-ftpe-workflow?${params.toString()}`);
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button className="bg-blue-500 hover:bg-blue-600">
          <UserPlus className="w-4 h-4 mr-2" />
          Créer l'adhésion FTPE
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-500" />
            Souscription FTPE Outremer pour un membre
          </DialogTitle>
        </DialogHeader>
        
        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-emerald-700 mb-2">Adhésion créée avec succès !</h3>
            <p className="text-gray-600">
              L'adhésion FTPE a été créée pour {selectedMember?.entreprise || selectedMember?.email}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Recherche de membre */}
            {!selectedMember ? (
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher un membre (email, entreprise, nom)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                  {searchQuery && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                      {filteredMembers.length} résultat(s)
                    </span>
                  )}
                </div>
                
                {/* Indicateur de données */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {members.length} membres disponibles
                  </span>
                  <button 
                    onClick={fetchMembers} 
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                  </button>
                </div>
                
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                    <p className="mt-2 text-sm text-gray-500">Chargement des membres...</p>
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Aucun membre trouvé</p>
                    {searchQuery && (
                      <p className="text-sm mt-1">Essayez de modifier votre recherche</p>
                    )}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2 bg-gray-50">
                    {filteredMembers.map((member) => (
                      <button
                        key={member.id || member._id}
                        onClick={() => selectMember(member)}
                        className="w-full p-3 border bg-white rounded-lg hover:border-blue-400 hover:bg-blue-50 text-left transition-all shadow-sm"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {member.entreprise || member.raison_sociale || 
                               (member.prenom && member.nom ? `${member.prenom} ${member.nom}` : 
                                member.nom || member.email || 'Membre sans nom')}
                            </p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                            {member.territoire && (
                              <p className="text-xs text-gray-400 mt-1">📍 {member.territoire}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {member.ftpe_status === 'active' ? (
                              <Badge className="bg-emerald-700 text-white font-semibold">FTPE Active</Badge>
                            ) : member.ftpe_status === 'to_renew' ? (
                              <Badge className="bg-orange-600 text-white font-semibold">À Renouveler</Badge>
                            ) : (
                              <Badge className="bg-gray-700 text-white font-semibold">Sans FTPE</Badge>
                            )}
                            {member.created_at && (
                              <span className="text-xs text-gray-400">
                                Inscrit le {new Date(member.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Formulaire de souscription */
              <div className="space-y-4">
                {/* Membre sélectionné */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Shield className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-semibold text-gray-900">{selectedMember.entreprise || `${selectedMember.prenom} ${selectedMember.nom}`}</p>
                          <p className="text-sm text-gray-600">{selectedMember.email}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedMember(null)}>
                        Changer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                    <AlertTriangle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                
                {/* Formulaire */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Raison sociale *</Label>
                    <Input
                      value={form.raison_sociale}
                      onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })}
                      placeholder="Nom de l'entreprise"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>SIRET</Label>
                    <Input
                      value={form.siret}
                      onChange={(e) => setForm({ ...form, siret: e.target.value })}
                      placeholder="14 chiffres"
                      maxLength={14}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <TerritorySelector
                      value={form.territoire}
                      onChange={(v) => setForm({ ...form, territoire: v })}
                      label="Territoire"
                      required
                      showDetails={false}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Adresse</Label>
                    <Input
                      value={form.adresse}
                      onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                      placeholder="Adresse complète"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <PhoneInputWithFlagSVG
                      value={form.telephone}
                      onChange={(val) => setForm({ ...form, telephone: val })}
                      label="Téléphone"
                      required
                      defaultCountry={form.territoire === 'Martinique' ? 'MQ' : 
                                     form.territoire === 'Guyane' ? 'GF' :
                                     form.territoire === 'La Réunion' ? 'RE' : 'GP'}
                    />
                  </div>
                  
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Représentant légal *</Label>
                    <Input
                      value={form.representant_nom}
                      onChange={(e) => setForm({ ...form, representant_nom: e.target.value })}
                      placeholder="Prénom NOM"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Type d'adhésion</Label>
                    <Select value={form.type_adhesion} onValueChange={(v) => setForm({ ...form, type_adhesion: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tpe">TPE (Très Petite Entreprise)</SelectItem>
                        <SelectItem value="pme">PME (Petite et Moyenne Entreprise)</SelectItem>
                        <SelectItem value="partenaire">Partenaire / Structure d'accompagnement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Prix */}
                <Card className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-6 h-6 text-blue-600" />
                        <div>
                          <p className="font-semibold text-gray-900">Cotisation annuelle</p>
                          <p className="text-sm text-gray-600">Adhésion FTPE Outremer</p>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">200 €</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowDialog(false)}>
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={submitting || !form.raison_sociale || !form.email || !form.representant_nom}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Créer l'adhésion FTPE
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
